import { useState } from "react";
import { toast } from "sonner";
import { PhoneCall } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Field, Honeypot, ConsentBox, SubmitBtn, SuccessBox, isEmail, isPhone } from "@/components/site/FormBits";

const INIT = { name: "", phone: "", email: "", interest: "", preferred_time: "", note: "", kvkk_ack: false, website: "" };
const TIMES = ["Hafta içi 09:00-12:00", "Hafta içi 12:00-15:00", "Hafta içi 15:00-18:00", "Hafta sonu", "Fark etmez"];

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Ad soyad girin.";
  if (!isPhone(v.phone)) e.phone = "Geçerli bir telefon numarası girin.";
  if (v.email && !isEmail(v.email)) e.email = "Geçerli bir e-posta adresi girin veya boş bırakın.";
  if (!v.interest) e.interest = "Konu seçin.";
  if (!v.kvkk_ack) e.kvkk_ack = "Aydınlatma metnini okuduğunuzu onaylayın.";
  return e;
}

function SelectFields({ v, set, errors, services }) {
  return (
    <>
      <Field id="cb-interest" label="İlgilendiğiniz konu" required error={errors.interest}>
        <select id="cb-interest" value={v.interest} onChange={set("interest")} className="field-input" data-testid="callback-interest">
          <option value="">Seçiniz</option>
          <option>İş başvurusu</option>
          {(services || []).map((s) => <option key={s.slug}>{s.title}</option>)}
          <option>Diğer</option>
        </select>
      </Field>
      <Field id="cb-preferred_time" label="Aranmak için uygun zaman">
        <select id="cb-preferred_time" value={v.preferred_time} onChange={set("preferred_time")} className="field-input" data-testid="callback-preferred_time">
          <option value="">Seçiniz</option>{TIMES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </Field>
    </>
  );
}

export default function CallbackForm({ defaultInterest = "" }) {
  const { services } = useSite();
  const [v, setV] = useState({ ...INIT, interest: defaultInterest });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setV({ ...v, [k]: e?.target ? e.target.value : e });
  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(v);
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const r = await api.post("/public/callback", { ...v, email: v.email || null });
      setDone(r.data.ref_no || "");
    } catch (err) {
      toast.error(errMsg(err, "Talebiniz kaydedilemedi. Lütfen tekrar deneyin veya bizi telefonla arayın."));
    } finally {
      setLoading(false);
    }
  };
  if (done !== null) return <SuccessBox testid="callback-success" title="Geri arama talebiniz alındı" text={`Kayıt numaranız: ${done}. Belirttiğiniz zaman aralığında sizi arayacağız.`} />;
  const input = (k, p = {}) => <input id={`cb-${k}`} value={v[k]} onChange={set(k)} className="field-input" data-testid={`callback-${k}`} {...p} />;
  return (
    <form onSubmit={submit} noValidate className="relative space-y-5 rounded-lg border border-zinc-200 bg-white p-5 sm:p-8" data-testid="callback-form">
      <h2 className="flex items-center gap-2 text-xl font-bold text-zinc-950"><PhoneCall className="h-5 w-5 text-[color:var(--red)]" /> Sizi arayalım</h2>
      <Honeypot value={v.website} onChange={set("website")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="cb-name" label="Ad Soyad" required error={errors.name}>{input("name", { autoComplete: "name" })}</Field>
        <Field id="cb-phone" label="Telefon" required error={errors.phone}>{input("phone", { type: "tel", inputMode: "tel" })}</Field>
        <Field id="cb-email" label="E-posta (isteğe bağlı)" error={errors.email}>{input("email", { type: "email" })}</Field>
        <SelectFields v={v} set={set} errors={errors} services={services} />
      </div>
      <Field id="cb-note" label="Görüşme notu">
        <textarea id="cb-note" rows={3} maxLength={1000} value={v.note} onChange={set("note")} className="field-input" data-testid="callback-note" />
      </Field>
      <ConsentBox id="callback_kvkk" checked={v.kvkk_ack} onChange={set("kvkk_ack")} error={errors.kvkk_ack} to="/kvkk-aydinlatma-metni" linkText="KVKK Aydınlatma Metni'ni" rest="okudum. *" />
      <SubmitBtn loading={loading} testid="callback-submit-btn">Beni Arayın</SubmitBtn>
    </form>
  );
}
